import { ID, Task, TaskCalendarOverride } from '@griply/types';
import { getTaskLinkedItemIds, LinkedItemGoal } from '@griply/utils';
import { useGoals } from 'features/goals';
import { useCallback } from 'react';
import { useSetTaskCalendarOverrideMutation } from 'shared/hooks/queries/use-set-task-calendar-override-mutation';

import { useExternalCalendarConnections } from './use-external-calendar-connections';

export type TaskCalendarOverrideSource = Pick<
  Task,
  'id' | 'goalId' | 'lifeAreaId'
>;

const mergeOverride = (
  current: TaskCalendarOverride,
  patch: Partial<TaskCalendarOverride>,
): TaskCalendarOverride | null => {
  const merged = { ...current, ...patch };

  if (merged.synced === undefined && merged.detailsVisibility === undefined) {
    return null;
  }

  return {
    ...(merged.synced !== undefined && { synced: merged.synced }),
    ...(merged.detailsVisibility !== undefined && {
      detailsVisibility: merged.detailsVisibility,
    }),
  };
};

export const useSetTaskCalendarOverride = () => {
  const { mutate } = useSetTaskCalendarOverrideMutation();
  const connections = useExternalCalendarConnections();
  const goals = useGoals();

  return useCallback(
    (
      task: TaskCalendarOverrideSource,
      patch: Partial<TaskCalendarOverride>,
    ) => {
      const goalsById = new Map<ID, LinkedItemGoal>(
        goals.map((goal) => [goal.id, goal]),
      );
      const linkedItemIds = getTaskLinkedItemIds(task, goalsById);

      connections.forEach((connection) => {
        const calendarSettings = connection.settings?.calendarSettings;
        if (
          !calendarSettings ||
          ![...linkedItemIds].some((id) => calendarSettings[id])
        ) {
          return;
        }

        const current = connection.settings?.taskOverrides?.[task.id] ?? {};
        mutate({
          connectionId: connection.id,
          taskId: task.id,
          override: mergeOverride(current, patch),
        });
      });
    },
    [mutate, connections, goals],
  );
};
